import React, { useState, useEffect, useRef } from 'react';
import { compressImage } from '../utils/imageCompression';
import { convertHeicFiles } from '../utils/convertHeic';
import InviteCollaboratorModal from './InviteCollaboratorModal';
import { Send, Image as ImageIcon, X, User, Plus, Search, ChevronDown, ChevronUp, Reply, Clock } from 'lucide-react';

const ProjectMessages = ({ projectId, shareToken, onNavigateToCollaborators, composeRequest, viewerType, viewerEmail, viewerName, projectLeadName }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [availableRecipients, setAvailableRecipients] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [uploadingReplyAttachments, setUploadingReplyAttachments] = useState(false);
  const [showNewMessageForm, setShowNewMessageForm] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState(''); // For searching messages
  const [recipientSearchQuery, setRecipientSearchQuery] = useState(''); // For searching recipients (designer view)
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteType, setInviteType] = useState('trade'); // Default to trade
  const [inviteMode, setInviteMode] = useState('manual'); // 'manual' or 'search'
  const [tradeCategory, setTradeCategory] = useState('contractor');
  const [messageVisibility, setMessageVisibility] = useState('all');
  const [scheduleAccess, setScheduleAccess] = useState(true);
  const [inviteSearchQuery, setInviteSearchQuery] = useState(''); // For invite modal business search
  const [searchLocation, setSearchLocation] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [designerLocation, setDesignerLocation] = useState('');
  const [imageModal, setImageModal] = useState(null); // { url, name } or null
  const [clientVisible, setClientVisible] = useState(true);
  const [tradeVisible, setTradeVisible] = useState(true);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [isDesigner, setIsDesigner] = useState(() => {
    const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('user') || '{}');
    return user.role === 'designer' || user.role === 'admin';
  });
  const isSharedViewer = !!shareToken;
  const isDesignerViewer = isDesigner && !isSharedViewer;
  const [composeMeta, setComposeMeta] = useState(null); // { recipientsOverride }
  const messagesEndRef = useRef(null);
  const recipientsInitRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!loading) {
      scrollToBottom();
    }
  }, [messages, loading]);

  const getAudienceLabelForSelection = (sel) => {
    const list = Array.isArray(sel) ? sel : [];
    if (list.includes('all')) return 'Everyone';
    if (list.includes('all_clients')) return 'Homeowners';
    if (list.includes('all_trades')) return 'Trades';
    const emails = list
      .filter(x => typeof x === 'string' && x.includes('@'))
      .slice(0, 4);
    if (emails.length > 0) return `Specific: ${emails.join(', ')}${list.length > 4 ? '…' : ''}`;
    return 'Custom';
  };

  const getSingleRecipientLabel = (val) => {
    if (!val) return isDesignerViewer ? 'Pick a contractor' : 'Everyone';
    if (val === 'all') return 'Everyone';
    if (val === 'all_clients') return 'Homeowners';
    if (val === 'all_trades') return 'All Trades (Announcement)';
    const match = availableRecipients.find((r) => (r.email || r.type) === val);
    return match?.label || getAudienceLabelForSelection([val]);
  };

  const getSharedAudienceLabel = () => {
    // Shared-link users should always feel like they're messaging the designer/GC.
    // Even though recipients are stored to control who else can see it, the UX is “to designer”.
    return projectLeadName ? `Designer (${projectLeadName})` : 'Designer';
  };

  const buildSelfRecipient = () => {
    if (!viewerEmail) return null;
    const type = viewerType === 'trade' ? 'trade' : viewerType === 'client' ? 'client' : 'client';
    return { type, email: viewerEmail, name: viewerName || viewerEmail };
  };

  const getTradeRecipientObject = (tradeEmail) => {
    if (!tradeEmail) return null;
    const match = availableRecipients.find((r) => r && r.type === 'trade' && r.email === tradeEmail);
    const name = match?.label || tradeEmail;
    return { type: 'trade', email: tradeEmail, name };
  };

  const getTradesList = () => {
    return (Array.isArray(availableRecipients) ? availableRecipients : [])
      .filter((r) => r && r.type === 'trade' && r.email)
      .map((r) => ({ email: r.email, label: r.label || r.email }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const activeThreadKey = Array.isArray(selectedRecipients) ? selectedRecipients[0] : null; // trade email or 'all_trades'
  const hasActiveThread = !!activeThreadKey;
  const isAnnouncementThread = activeThreadKey === 'all_trades';

  const resolveSelectionToRecipients = (sel) => {
    const list = Array.isArray(sel) ? sel : [];
    const out = [];
    for (const val of list) {
      if (!val) continue;
      if (val === 'all' || val === 'all_trades' || val === 'all_clients') {
        out.push(val);
        continue;
      }
      if (typeof val === 'string' && val.includes('@')) {
        const match = availableRecipients.find((r) => r && r.email === val);
        if (match) {
          out.push({ type: match.type || 'client', email: match.email, name: match.label || match.email });
        } else {
          // Fallback: keep as string (server will treat as client).
          out.push(val);
        }
      } else {
        out.push(val);
      }
    }
    return out;
  };

  const normalizeRecipientsForSend = (list) => {
    const raw = Array.isArray(list) ? list : [];
    const normalized = [];
    for (const r of raw) {
      if (!r) continue;
      if (typeof r === 'string') {
        normalized.push(r);
        continue;
      }
      if (typeof r === 'object') {
        // support both {type,email,name} and DB-shaped recipients {recipient_type,recipient_email,recipient_name}
        const type = r.type || r.recipient_type;
        const email = r.email || r.recipient_email;
        const name = r.name || r.recipient_name || email;
        if (type === 'all' || type === 'all_clients' || type === 'all_trades') {
          normalized.push(type);
        } else if (email) {
          normalized.push({ type: type || 'client', email, name });
        }
      }
    }
    return normalized;
  };

  const getRecipientsForReply = (parentId, fallbackRecipients) => {
    const parent = messages.find((m) => m.id === parentId);
    const parentRecipients = normalizeRecipientsForSend(parent?.recipients || []);
    if (parentRecipients.length > 0) return parentRecipients;
    return fallbackRecipients;
  };


  const API_BASE_URL = process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

  useEffect(() => {
    loadMessages();
    loadRecipients();
  }, [projectId, shareToken]);

  // Shared-link users: keep message sending simple (no recipient picking)
  useEffect(() => {
    if (isSharedViewer) {
      // Keep state consistent, but do not imply “broadcast to trades/clients”.
      // We store the sender as a recipient so only they (and the designer) see the thread.
      const self = buildSelfRecipient();
      setSelectedRecipients(self?.email ? [self.email] : ['all']);
    }
  }, [isSharedViewer, viewerType, viewerEmail, viewerName]);

  // Allow parent (e.g., Schedule tab) to open composer with a pre-filled message.
  // We avoid overwriting an in-progress draft.
  useEffect(() => {
    const text = (composeRequest?.text || '').toString();
    const nonce = composeRequest?.nonce;
    if (!nonce || !text.trim()) return;

    setShowNewMessageForm(true);
    setNewMessage((prev) => (prev && prev.trim() ? prev : text));
    setComposeMeta({
      recipientsOverride: Array.isArray(composeRequest?.recipientsOverride) ? composeRequest.recipientsOverride : null
    });
  }, [composeRequest?.nonce]);

  // Reload recipients when form is shown
  useEffect(() => {
    if (showNewMessageForm && availableRecipients.length === 0) {
      loadRecipients();
    }
  }, [showNewMessageForm]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const url = shareToken
        ? `${API_BASE_URL}/messages/${projectId}?shareToken=${shareToken}`
        : `${API_BASE_URL}/messages/${projectId}`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && !shareToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Failed to load messages');
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecipients = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const url = shareToken
        ? `${API_BASE_URL}/messages/${projectId}/recipients?shareToken=${shareToken}`
        : `${API_BASE_URL}/messages/${projectId}/recipients`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && !shareToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Failed to load recipients');
      const data = await response.json();
      setAvailableRecipients(data.recipients || []);

      // Initialize selection once for designers (shared viewers have no picker).
      if (!isSharedViewer && !recipientsInitRef.current) {
        recipientsInitRef.current = true;
        // Messenger-like: always require picking a trade first (no remembered thread).
        setSelectedRecipients([]);
        setShowNewMessageForm(true);
      }
    } catch (error) {
      console.error('Error loading recipients:', error);
    }
  };


  const handleFileSelect = async (e, isReply = false) => {
    let files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (isReply) {
      setUploadingReplyAttachments(true);
    } else {
      setUploadingAttachments(true);
    }

    try {
      files = await convertHeicFiles(files);
      const compressedFiles = await Promise.all(
        files.map(file => compressImage(file, 1920, 1920, 0.8))
      );

      const uploadPromises = compressedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        if (shareToken) formData.append('shareToken', shareToken);

        const token = localStorage.getItem('authToken');
        const headers = {};
        if (token && !shareToken) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/upload/comment-attachment`, {
          method: 'POST',
          headers,
          body: formData
        });

        if (!response.ok) throw new Error('Failed to upload file');
        const data = await response.json();
        return {
          url: data.url,
          filename: data.filename || file.name,
          type: data.type || file.type,
          size: data.size || file.size
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      if (isReply) {
        setReplyAttachments([...replyAttachments, ...uploadedFiles]);
      } else {
        setAttachments([...attachments, ...uploadedFiles]);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload some files. Please try again.');
    } finally {
      if (isReply) {
        setUploadingReplyAttachments(false);
      } else {
        setUploadingAttachments(false);
      }
      e.target.value = '';
    }
  };

  const removeAttachment = (index, isReply = false) => {
    if (isReply) {
      setReplyAttachments(replyAttachments.filter((_, i) => i !== index));
    } else {
      setAttachments(attachments.filter((_, i) => i !== index));
    }
  };

  const handleSendMessage = async (parentId = null) => {
    const messageAttachments = parentId ? replyAttachments : attachments;
    if (!newMessage.trim() && messageAttachments.length === 0) return;

    try {
      const token = localStorage.getItem('authToken');
      const url = shareToken
        ? `${API_BASE_URL}/messages/${projectId}?shareToken=${shareToken}`
        : `${API_BASE_URL}/messages/${projectId}`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && !shareToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Build mentions array from selectedMentions
      const mentions = selectedMentions.map(mention => ({
        email: mention.email,
        name: mention.name || mention.label,
        type: mention.type
      }));

      const defaultSharedRecipients =
        (() => {
          const self = buildSelfRecipient();
          return self ? [self] : ['all'];
        })();

      // If we have an explicit "direct" recipients list (e.g., schedule request),
      // use it for this send only.
      const selectedKey = Array.isArray(selectedRecipients) ? selectedRecipients[0] : null;
      const designerRecipientsForSelection =
        selectedKey === 'all_trades'
          ? ['all_trades']
          : (typeof selectedKey === 'string' && selectedKey.includes('@'))
            ? [getTradeRecipientObject(selectedKey)]
            : [];

      const recipientsForNewMessage =
        (composeMeta?.recipientsOverride && composeMeta.recipientsOverride.length > 0)
          ? composeMeta.recipientsOverride
          : (isSharedViewer
              ? defaultSharedRecipients
              : (isDesignerViewer ? designerRecipientsForSelection : resolveSelectionToRecipients(selectedRecipients)));

      const recipientsForSend =
        parentId
          ? getRecipientsForReply(parentId, recipientsForNewMessage)
          : recipientsForNewMessage;

      // Designers must pick a trade before sending (Messenger-like).
      if (isDesignerViewer && !parentId && (!recipientsForSend || recipientsForSend.length === 0)) {
        setShowNewMessageForm(true);
        return;
      }

      // Guardrail: confirm before sending a broadcast on mobile.
      if (isDesigner && !isSharedViewer && !parentId) {
        const flat = Array.isArray(recipientsForSend) ? recipientsForSend : [];
        const broad = flat.some((r) => r === 'all_trades');
        if (broad && typeof window !== 'undefined' && window.innerWidth <= 640) {
          const label =
            flat.includes('all_trades') ? 'All Trades' :
            'everyone';
          const ok = window.confirm(`Send this message to ${label}?`);
          if (!ok) return;
        }
      }

      const body = {
        messageText: newMessage,
        parentId: parentId,
        recipients: recipientsForSend,
        attachments: messageAttachments,
        clientVisible: isDesigner ? clientVisible : true, // Only designers can control visibility
        tradeVisible: isDesigner ? tradeVisible : true,
        mentions: (!isSharedViewer && mentions.length > 0) ? mentions : undefined
      };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Failed to send message');
      
      setNewMessage('');
      setAttachments([]);
      setReplyAttachments([]);
      setReplyingTo(null);
      setClientVisible(true);
      setTradeVisible(true);
      setSelectedMentions([]);
      setShowNewMessageForm(false);
      setComposeMeta(null);
      loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    }
  };

  const getThreadedMessages = () => {
    const messageMap = new Map();
    const rootMessages = [];

    // Filter messages by search query if provided
    let filteredMessages = messages;

    // Designer UX: show one trade conversation at a time (Messenger-like).
    if (isDesignerViewer) {
      const selectedKey = Array.isArray(selectedRecipients) ? selectedRecipients[0] : null;
      if (selectedKey === 'all_trades') {
        filteredMessages = filteredMessages.filter((msg) => {
          const recips = Array.isArray(msg.recipients) ? msg.recipients : [];
          return recips.some((r) => r && r.recipient_type === 'all_trades');
        });
      } else if (typeof selectedKey === 'string' && selectedKey.includes('@')) {
        const email = selectedKey.toLowerCase();
        filteredMessages = filteredMessages.filter((msg) => {
          if (msg.sender_email && msg.sender_email.toLowerCase() === email) return true;
          const recips = Array.isArray(msg.recipients) ? msg.recipients : [];
          return recips.some((r) => (r?.recipient_email || '').toLowerCase() === email);
        });
      } else {
        // No conversation selected yet.
        filteredMessages = [];
      }
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredMessages = filteredMessages.filter(msg => 
        msg.message_text?.toLowerCase().includes(query) ||
        msg.sender_name?.toLowerCase().includes(query)
      );
    }

    filteredMessages.forEach(msg => {
      messageMap.set(msg.id, { ...msg, replies: [] });
    });

    filteredMessages.forEach(msg => {
      if (msg.parent_id) {
        const parent = messageMap.get(msg.parent_id);
        if (parent) {
          parent.replies.push(msg);
        }
      } else {
        rootMessages.push(messageMap.get(msg.id));
      }
    });

    return rootMessages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const handleInviteCollaborator = async (projectIdParam) => {
    if (!inviteEmail.trim() || !inviteName.trim()) {
      alert('Please enter both name and email');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token && !shareToken) {
        alert('You must be logged in to invite collaborators');
        return;
      }

      const pid = projectIdParam || projectId;
      if (!pid) {
        alert('Project ID not found');
        return;
      }

      const url = shareToken
        ? `${API_BASE_URL}/collaboration/invite-trade?shareToken=${shareToken}`
        : `${API_BASE_URL}/collaboration/${inviteType === 'trade' ? 'invite-trade' : 'share'}`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token && !shareToken) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const body = inviteType === 'trade'
        ? {
            projectId: pid,
            tradeEmail: inviteEmail,
            tradeName: inviteName,
            tradeType: tradeCategory,
            accessLevel: 'comment',
            message: 'You have been invited to collaborate on this project',
            messageVisibility: messageVisibility,
            scheduleAccess: scheduleAccess
          }
        : {
            projectId: pid,
            recipientEmail: inviteEmail,
            recipientName: inviteName,
            accessLevel: 'comment',
            message: 'You have been invited to view and collaborate on this project',
            messageVisibility: messageVisibility,
            scheduleAccess: true
          };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to invite collaborator');
      }
      
      const data = await response.json();
      const shareTokenResult = data.share?.shareToken || data.trade?.share_token || data.share_token;
      const shareUrl =
        inviteType === 'trade'
          ? `${window.location.origin}/shared/${shareTokenResult}/communication`
          : `${window.location.origin}/shared/${shareTokenResult}`;
      
      // Reset form
      setInviteEmail('');
      setInviteName('');
      setInviteType('trade');
      setTradeCategory('contractor');
      setMessageVisibility('all');
      setScheduleAccess(true);
      setShowInviteModal(false);
      
      // Show success
      const emailBody = `Hi ${inviteName},\n\nYou have been invited to collaborate on a project.\n\nPlease click the link below to access:\n\n${shareUrl}\n\nThank you!`;
      const mailtoLink = `mailto:${inviteEmail}?subject=Project Collaboration Invitation&body=${encodeURIComponent(emailBody)}`;
      
      const userChoice = window.confirm(
        `Invitation created!\n\n` +
        `Link: ${shareUrl}\n\n` +
        `Click OK to open your email client with the invitation, or Cancel to just copy the link.`
      );
      
      if (userChoice) {
        window.location.href = mailtoLink;
      } else {
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
      
      loadRecipients(); // Refresh available recipients
    } catch (error) {
      console.error('Error inviting collaborator:', error);
      alert(error.message || 'Failed to invite collaborator. Please try again.');
    }
  };

  const loadDesignerLocation = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await fetch(`${API_BASE_URL}/designers/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const profile = await response.json();
          const location = [];
          if (profile.city) location.push(profile.city);
          if (profile.provinceState) location.push(profile.provinceState);
          if (profile.country) location.push(profile.country);
          if (location.length > 0) {
            setDesignerLocation(location.join(', '));
            setSearchLocation(location.join(', '));
          }
        }
      }
    } catch (err) {
      console.error('Error loading designer location:', err);
    }
  };

  const handleSearchBusinesses = async (e) => {
    e.preventDefault();
    if (!inviteSearchQuery.trim()) {
      alert('Please enter a business name or type');
      return;
    }

    setSearching(true);
    try {
      const token = localStorage.getItem('authToken');
      
      const params = new URLSearchParams({
        query: inviteSearchQuery,
        ...(searchLocation && { location: searchLocation }),
        ...(searchType !== 'all' && { type: searchType })
      });

      const response = await fetch(`${API_BASE_URL}/google-business/search?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) throw new Error('Search failed');
      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (error) {
      console.error('Error searching businesses:', error);
      alert('Failed to search businesses. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleInviteFromSearch = (business) => {
    setInviteName(business.name || '');
    setInviteEmail(business.email || '');
    setInviteType('trade');
    if (business.business_type || business.type) {
      setTradeCategory(business.business_type || business.type || 'contractor');
    } else if (searchType && searchType !== 'all') {
      setTradeCategory(searchType);
    } else {
      setTradeCategory('contractor');
    }
    setInviteMode('manual');
  };

  const handleAddBusiness = async (businessData) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/google-business/local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(businessData)
      });

      if (!response.ok) {
        throw new Error('Failed to add business');
      }

      alert('Business added! It will now appear in search results.');
      
      // Refresh search if there was a query
      if (inviteSearchQuery.trim()) {
        setSearching(true);
        try {
          const params = new URLSearchParams({
            query: inviteSearchQuery,
            ...(searchLocation && { location: searchLocation }),
            ...(searchType !== 'all' && { type: searchType })
          });

          const refreshResponse = await fetch(`${API_BASE_URL}/google-business/search?${params}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setSearchResults(refreshData.results || []);
          }
        } catch (err) {
          console.error('Error refreshing search:', err);
        } finally {
          setSearching(false);
        }
      }
    } catch (error) {
      alert('Failed to add business. Please try again.');
      console.error('Error adding business:', error);
      throw error;
    }
  };


  const formatRecipients = (recipients) => {
    if (!recipients || recipients.length === 0) return 'Everyone';
    if (recipients.length === 1 && recipients[0].recipient_type === 'all') return 'Everyone';
    
    const types = recipients.map(r => {
      if (r.recipient_type === 'all') return 'Everyone';
      if (r.recipient_type === 'all_clients') return 'All Clients';
      if (r.recipient_type === 'all_trades') return 'All Trades';
      return r.recipient_name || r.recipient_email;
    });
    
    return types.join(', ');
  };

  const threadedMessages = getThreadedMessages();

  // ============================================
  // Designer Trades Chat (Messenger-like)
  // ============================================
  if (isDesignerViewer) {
    const trades = getTradesList().filter((t) => {
      const q = recipientSearchQuery.trim().toLowerCase();
      if (!q) return true;
      return t.label.toLowerCase().includes(q) || t.email.toLowerCase().includes(q);
    });

    const openTrade = (email) => {
      setSelectedRecipients([email]);
      setShowNewMessageForm(false);
      // Clear any previous message search when switching threads
      setSearchQuery('');
    };

    const openAnnouncement = () => {
      setSelectedRecipients(['all_trades']);
      setShowNewMessageForm(false);
      setSearchQuery('');
    };

    return (
      <>
        <style>{`
          /* Let parent control height; keep it flexible for drawers/mobile. */
          .chat-container {
            height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }
        `}</style>

        <div className="chat-container bg-gray-100 rounded-lg overflow-hidden shadow-inner">
          {/* Top header */}
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider truncate">
                Trades Chat
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {typeof onNavigateToCollaborators === 'function' && (
                <button
                  type="button"
                  onClick={onNavigateToCollaborators}
                  className="px-3 py-2 text-xs font-bold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  title="Manage collaborators"
                >
                  Collaborators
                </button>
              )}
            </div>
          </div>

          {/* Two-pane layout (desktop) / single-pane (mobile) */}
          <div className="flex-1 min-h-0 flex">
            {/* Left: trade list */}
            <aside className={`w-full sm:w-80 bg-white border-r border-gray-200 flex flex-col min-h-0 ${hasActiveThread ? 'hidden sm:flex' : 'flex'}`}>
              <div className="p-3 border-b border-gray-100">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-xs font-semibold text-gray-600">Pick a contractor</div>
                  {/* Secondary action (intentionally out of the way) */}
                  {availableRecipients.some((r) => (r.email || r.type) === 'all_trades') && (
                    <button
                      type="button"
                      onClick={openAnnouncement}
                      className="text-xs font-bold text-blue-600 hover:underline"
                      title="Send an announcement to all trades"
                    >
                      Announcement
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={recipientSearchQuery}
                  onChange={(e) => setRecipientSearchQuery(e.target.value)}
                  placeholder="Search trades…"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-green-500"
                />
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-2">
                {trades.length === 0 ? (
                  <div className="text-sm text-gray-500 py-10 text-center px-4">
                    {getTradesList().length === 0 ? 'No trades invited yet.' : 'No matches.'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {trades.map((t) => (
                      <button
                        key={t.email}
                        type="button"
                        onClick={() => openTrade(t.email)}
                        className="w-full text-left px-3 py-3 rounded-xl border border-gray-200 bg-white hover:border-green-300 transition-colors"
                      >
                        <div className="font-semibold text-gray-800 truncate">{t.label}</div>
                        <div className="text-xs text-gray-500 truncate">{t.email}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </aside>

            {/* Right: conversation */}
            <main className={`flex-1 min-h-0 flex flex-col ${hasActiveThread ? 'flex' : 'hidden sm:flex'}`}>
              {/* Thread header (shows on mobile too) */}
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRecipients([]);
                      setReplyingTo(null);
                      setSearchQuery('');
                    }}
                    className="sm:hidden px-2 py-1 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Back"
                  >
                    ← Trades
                  </button>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-800 truncate">
                      {isAnnouncementThread ? 'Announcement (All Trades)' : getSingleRecipientLabel(activeThreadKey)}
                    </div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {isAnnouncementThread ? 'Broadcast to every trade' : 'Private thread'}
                    </div>
                  </div>
                </div>

                {/* Tiny affordance to switch threads on desktop */}
                <button
                  type="button"
                  onClick={() => setSelectedRecipients([])}
                  className="hidden sm:inline-flex px-3 py-2 text-xs font-bold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  title="Switch trade"
                >
                  Switch
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                {loading ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-2"></div>
                    <p className="text-sm">Loading chat...</p>
                  </div>
                ) : threadedMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 opacity-60">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                      <ImageIcon size={32} />
                    </div>
                    <p className="text-sm font-medium">No messages yet</p>
                    <p className="text-xs">Send the first message below</p>
                  </div>
                ) : (
                  <>
                    {threadedMessages.map((message) => {
                      const isMe = (viewerEmail && message.sender_email === viewerEmail) || 
                                   (viewerName && message.sender_name === viewerName);
                      
                      return (
                        <div key={message.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                          {/* Sender & Time Info */}
                          {!isMe && (
                            <div className="flex items-center gap-1.5 mb-1 px-1">
                              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">{message.sender_name}</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                                message.sender_type === 'designer' ? 'bg-blue-50 text-blue-600' :
                                message.sender_type === 'client' ? 'bg-amber-50 text-amber-600' :
                                'bg-orange-50 text-orange-600'
                              }`}>
                                {message.sender_type}
                              </span>
                            </div>
                          )}

                          {/* Message Bubble Container */}
                          <div className="flex items-end gap-2 max-w-[85%]">
                            {!isMe && (
                              <div className="shrink-0 w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-[10px] font-bold text-white mb-1">
                                {message.sender_name?.charAt(0)}
                              </div>
                            )}
                            
                            <div className="flex flex-col gap-1">
                              <div 
                                className={`rounded-2xl px-4 py-2.5 shadow-sm relative group ${
                                  isMe 
                                    ? 'bg-green-600 text-white rounded-tr-none' 
                                    : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap leading-relaxed">
                                  {message.message_text}
                                </p>

                                {/* Attachments within bubble */}
                                {message.attachments && message.attachments.length > 0 && (
                                  <div className={`mt-2 grid gap-1 ${message.attachments.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                    {message.attachments.map((attachment, idx) => (
                                      <div key={idx} className="rounded-lg overflow-hidden border border-black/10">
                                        {attachment.file_type && attachment.file_type.startsWith('image/') ? (
                                          <img 
                                            src={attachment.file_url} 
                                            alt={attachment.file_name} 
                                            className="w-full aspect-square object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                                            onClick={() => setImageModal({ url: attachment.file_url, name: attachment.file_name })}
                                          />
                                        ) : (
                                          <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-black/5 text-[10px] truncate">
                                            <ImageIcon size={12} />
                                            <span className="truncate">{attachment.file_name}</span>
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Reply Trigger */}
                                <button
                                  onClick={() => setReplyingTo(replyingTo === message.id ? null : message.id)}
                                  className={`absolute -bottom-6 ${isMe ? 'right-0' : 'left-0'} p-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-gray-200 rounded-full shadow-sm text-gray-500 hover:text-green-600 z-10`}
                                >
                                  <Reply size={12} />
                                </button>
                              </div>
                              
                              <div className={`flex items-center gap-2 mt-0.5 px-1 ${isMe ? 'justify-end' : ''}`}>
                                <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
                                  <Clock size={8} />
                                  {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {!isMe && (
                                  <button 
                                    onClick={() => setReplyingTo(replyingTo === message.id ? null : message.id)}
                                    className="text-[9px] font-bold text-green-600 uppercase hover:underline"
                                  >
                                    Reply
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Inline Reply Form */}
                          {replyingTo === message.id && (
                            <div className="w-full mt-2 pl-8">
                              <div className="bg-white rounded-xl p-3 border border-green-200 shadow-md">
                                <div className="flex items-center gap-2 mb-2">
                                  <Reply size={12} className="text-green-600" />
                                  <span className="text-[10px] font-bold text-gray-500 uppercase">Replying to {message.sender_name}</span>
                                </div>
                                <textarea
                                  autoFocus
                                  placeholder="Type your reply..."
                                  value={newMessage}
                                  onChange={(e) => setNewMessage(e.target.value)}
                                  className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-green-500 min-h-[60px] resize-none"
                                />
                                <div className="flex justify-between items-center mt-2">
                                  <div className="flex gap-2">
                                    <input
                                      type="file"
                                      id={`reply-img-${message.id}`}
                                      className="hidden"
                                      onChange={(e) => handleFileSelect(e, true)}
                                      multiple
                                    />
                                    <label htmlFor={`reply-img-${message.id}`} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full cursor-pointer">
                                      <ImageIcon size={18} />
                                    </label>
                                  </div>
                                  <div className="flex gap-2">
                                    <button onClick={() => setReplyingTo(null)} className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase hover:bg-gray-100 rounded-lg">Cancel</button>
                                    <button 
                                      onClick={() => handleSendMessage(message.id)}
                                      disabled={!newMessage.trim() && replyAttachments.length === 0}
                                      className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold uppercase disabled:opacity-50"
                                    >
                                      Send
                                    </button>
                                  </div>
                                </div>
                                {replyAttachments.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {replyAttachments.map((at, i) => (
                                      <div key={i} className="relative group w-12 h-12">
                                        <img src={at.url} className="w-full h-full object-cover rounded border border-gray-200" alt="" />
                                        <button onClick={() => removeAttachment(i, true)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow-sm"><X size={10} /></button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Nested Replies */}
                          {message.replies && message.replies.length > 0 && (
                            <div className="w-full mt-2 pl-8 space-y-3">
                              {message.replies.map((reply) => {
                                const replyIsMe = (viewerEmail && reply.sender_email === viewerEmail) || 
                                                 (viewerName && reply.sender_name === viewerName);
                                return (
                                  <div key={reply.id} className={`flex flex-col ${replyIsMe ? 'items-end' : 'items-start'}`}>
                                    <div className={`rounded-xl px-3 py-1.5 shadow-sm text-xs ${
                                      replyIsMe ? 'bg-green-100 text-green-900 border border-green-200' : 'bg-white text-gray-700 border border-gray-200'
                                    }`}>
                                      {!replyIsMe && <span className="font-bold mr-1.5">{reply.sender_name}:</span>}
                                      {reply.message_text}
                                      {reply.attachments && reply.attachments.length > 0 && (
                                        <div className="mt-1 flex gap-1 overflow-x-auto pb-1">
                                          {reply.attachments.map((at, i) => (
                                            <img key={i} src={at.file_url} className="h-10 w-10 object-cover rounded cursor-pointer" onClick={() => setImageModal({url: at.file_url, name: at.file_name})} alt="" />
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <span className="text-[8px] text-gray-400 mt-0.5 px-1">
                                      {new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input bar (simple) */}
              <div className="shrink-0 bg-white border-t border-gray-200 p-3">
                <div className="flex items-end gap-2 bg-gray-50 rounded-2xl p-1 border border-gray-200 focus-within:border-green-400 focus-within:ring-1 focus-within:ring-green-400 transition-all">
                  <div className="shrink-0 flex items-center mb-1 ml-1">
                    <input
                      type="file"
                      id="main-chat-img-designer"
                      className="hidden"
                      multiple
                      onChange={handleFileSelect}
                      disabled={uploadingAttachments || (!composeMeta?.recipientsOverride && !hasActiveThread)}
                    />
                    <label htmlFor="main-chat-img-designer" className="p-2.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-full cursor-pointer transition-colors">
                      <ImageIcon size={22} />
                    </label>
                  </div>

                  <textarea
                    placeholder={hasActiveThread ? 'Type a message…' : 'Pick a contractor to start chatting…'}
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      e.target.style.height = 'inherit';
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && window.innerWidth > 640) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={!composeMeta?.recipientsOverride && !hasActiveThread}
                    className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-2.5 px-1 max-h-[120px] resize-none overflow-y-auto disabled:opacity-60"
                    style={{ height: '40px' }}
                  />

                  <button
                    onClick={() => handleSendMessage()}
                    disabled={(!newMessage.trim() && attachments.length === 0) || uploadingAttachments || (!composeMeta?.recipientsOverride && !hasActiveThread)}
                    className="shrink-0 mb-1 mr-1 p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700 disabled:bg-gray-300 disabled:hover:bg-gray-300 transition-all shadow-md active:scale-95"
                  >
                    <Send size={20} />
                  </button>
                </div>
              </div>
            </main>
          </div>
        </div>

        {/* Image modal remains shared */}
        {imageModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
            onClick={() => setImageModal(null)}
          >
            <div className="relative max-w-full max-h-full">
              <button
                onClick={() => setImageModal(null)}
                className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl font-bold z-10"
                aria-label="Close"
              >
                ×
              </button>
              <img 
                src={imageModal.url} 
                alt={imageModal.name} 
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
              {imageModal.name && (
                <p className="text-white text-center mt-3 text-sm font-medium">{imageModal.name}</p>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <style>{`
        .recipient-select-scroll::-webkit-scrollbar {
          width: 10px;
        }
        .recipient-select-scroll::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 4px;
        }
        .recipient-select-scroll::-webkit-scrollbar-thumb {
          background: #9ca3af;
          border-radius: 4px;
        }
        .recipient-select-scroll::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
        .chat-container {
          height: 100%;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        @media (max-width: 640px) {
          .chat-container {
            height: 100%;
          }
        }
      `}</style>
      <div className="chat-container bg-gray-100 rounded-lg overflow-hidden shadow-inner">
        {/* Header - Compact */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-2">
            {isDesignerViewer && !composeMeta?.recipientsOverride && selectedRecipients?.[0] && (
              <button
                type="button"
                onClick={() => {
                  setSelectedRecipients([]);
                  setShowNewMessageForm(true);
                }}
                className="px-2 py-1 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Back to trades"
              >
                ← Trades
              </button>
            )}

            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
              Trades Chat
              {isDesignerViewer && !composeMeta?.recipientsOverride && selectedRecipients?.[0] ? (
                <span className="ml-2 text-gray-500 normal-case tracking-normal font-semibold">
                  • {getSingleRecipientLabel(selectedRecipients?.[0])}
                </span>
              ) : null}
            </h2>
            <div className="flex -space-x-2">
              {availableRecipients.slice(0, 3).map((r, i) => (
                <div key={i} className="w-6 h-6 rounded-full bg-green-100 border-2 border-white flex items-center justify-center" title={r.label}>
                  <User size={12} className="text-green-600" />
                </div>
              ))}
              {availableRecipients.length > 3 && (
                <div className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[10px] font-bold text-gray-600">
                  +{availableRecipients.length - 3}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {!isSharedViewer && (
              <button
                onClick={() => {
                  setShowInviteModal(true);
                  loadDesignerLocation();
                }}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title="Invite people"
              >
                <Plus size={20} />
              </button>
            )}
            <button
              onClick={() => {
                const searchEl = document.getElementById('chat-search');
                if (searchEl) searchEl.focus();
              }}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Search size={20} />
            </button>
          </div>
        </div>

        {/* Search Bar - Minimal */}
        <div className="px-4 py-1 bg-white border-b border-gray-100 shrink-0">
          <div className="relative">
            <input
              id="chat-search"
              type="text"
              placeholder="Filter chat..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1 bg-gray-100 border-none rounded-full text-xs focus:ring-1 focus:ring-green-500"
            />
            <Search className="absolute left-2.5 top-1.5 h-3.5 w-3.5 text-gray-400" />
          </div>
        </div>

        {/* Messages List - Main Scroll Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-2"></div>
              <p className="text-sm">Loading chat...</p>
            </div>
          ) : threadedMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 opacity-60">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                <ImageIcon size={32} />
              </div>
              <p className="text-sm font-medium">No messages yet</p>
              <p className="text-xs">Start the conversation below</p>
            </div>
          ) : (
            <>
              {threadedMessages.map((message) => {
                const isMe = (viewerEmail && message.sender_email === viewerEmail) || 
                             (viewerName && message.sender_name === viewerName);
                
                return (
                  <div key={message.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                    {/* Sender & Time Info */}
                    {!isMe && (
                      <div className="flex items-center gap-1.5 mb-1 px-1">
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">{message.sender_name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                          message.sender_type === 'designer' ? 'bg-blue-50 text-blue-600' :
                          message.sender_type === 'client' ? 'bg-amber-50 text-amber-600' :
                          'bg-orange-50 text-orange-600'
                        }`}>
                          {message.sender_type}
                        </span>
                      </div>
                    )}

                    {/* Message Bubble Container */}
                    <div className="flex items-end gap-2 max-w-[85%]">
                      {!isMe && (
                        <div className="shrink-0 w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-[10px] font-bold text-white mb-1">
                          {message.sender_name?.charAt(0)}
                        </div>
                      )}
                      
                      <div className="flex flex-col gap-1">
                        <div 
                          className={`rounded-2xl px-4 py-2.5 shadow-sm relative group ${
                            isMe 
                              ? 'bg-green-600 text-white rounded-tr-none' 
                              : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">
                            {message.message_text}
                          </p>

                          {/* Attachments within bubble */}
                          {message.attachments && message.attachments.length > 0 && (
                            <div className={`mt-2 grid gap-1 ${message.attachments.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                              {message.attachments.map((attachment, idx) => (
                                <div key={idx} className="rounded-lg overflow-hidden border border-black/10">
                                  {attachment.file_type && attachment.file_type.startsWith('image/') ? (
                                    <img 
                                      src={attachment.file_url} 
                                      alt={attachment.file_name} 
                                      className="w-full aspect-square object-cover cursor-pointer hover:opacity-90 transition-opacity" 
                                      onClick={() => setImageModal({ url: attachment.file_url, name: attachment.file_name })}
                                    />
                                  ) : (
                                    <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-black/5 text-[10px] truncate">
                                      <ImageIcon size={12} />
                                      <span className="truncate">{attachment.file_name}</span>
                                    </a>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Reply Trigger Overlay (Mobile Friendly) */}
                          <button
                            onClick={() => setReplyingTo(replyingTo === message.id ? null : message.id)}
                            className={`absolute -bottom-6 ${isMe ? 'right-0' : 'left-0'} p-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-gray-200 rounded-full shadow-sm text-gray-500 hover:text-green-600 z-10`}
                          >
                            <Reply size={12} />
                          </button>
                        </div>
                        
                        <div className={`flex items-center gap-2 mt-0.5 px-1 ${isMe ? 'justify-end' : ''}`}>
                          <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
                            <Clock size={8} />
                            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {!isMe && (
                            <button 
                              onClick={() => setReplyingTo(replyingTo === message.id ? null : message.id)}
                              className="text-[9px] font-bold text-green-600 uppercase hover:underline"
                            >
                              Reply
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Inline Reply Form */}
                    {replyingTo === message.id && (
                      <div className="w-full mt-2 pl-8">
                        <div className="bg-white rounded-xl p-3 border border-green-200 shadow-md">
                          <div className="flex items-center gap-2 mb-2">
                            <Reply size={12} className="text-green-600" />
                            <span className="text-[10px] font-bold text-gray-500 uppercase">Replying to {message.sender_name}</span>
                          </div>
                          <textarea
                            autoFocus
                            placeholder="Type your reply..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            className="w-full p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-green-500 min-h-[60px] resize-none"
                          />
                          <div className="flex justify-between items-center mt-2">
                            <div className="flex gap-2">
                              <input
                                type="file"
                                id={`reply-img-${message.id}`}
                                className="hidden"
                                onChange={(e) => handleFileSelect(e, true)}
                                multiple
                              />
                              <label htmlFor={`reply-img-${message.id}`} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full cursor-pointer">
                                <ImageIcon size={18} />
                              </label>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => setReplyingTo(null)} className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase hover:bg-gray-100 rounded-lg">Cancel</button>
                              <button 
                                onClick={() => handleSendMessage(message.id)}
                                disabled={!newMessage.trim() && replyAttachments.length === 0}
                                className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-xs font-bold uppercase disabled:opacity-50"
                              >
                                Send
                              </button>
                            </div>
                          </div>
                          {replyAttachments.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {replyAttachments.map((at, i) => (
                                <div key={i} className="relative group w-12 h-12">
                                  <img src={at.url} className="w-full h-full object-cover rounded border border-gray-200" alt="" />
                                  <button onClick={() => removeAttachment(i, true)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow-sm"><X size={10} /></button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Nested Replies */}
                    {message.replies && message.replies.length > 0 && (
                      <div className="w-full mt-2 pl-8 space-y-3">
                        {message.replies.map((reply) => {
                          const replyIsMe = (viewerEmail && reply.sender_email === viewerEmail) || 
                                           (viewerName && reply.sender_name === viewerName);
                          return (
                            <div key={reply.id} className={`flex flex-col ${replyIsMe ? 'items-end' : 'items-start'}`}>
                              <div className={`rounded-xl px-3 py-1.5 shadow-sm text-xs ${
                                replyIsMe ? 'bg-green-100 text-green-900 border border-green-200' : 'bg-white text-gray-700 border border-gray-200'
                              }`}>
                                {!replyIsMe && <span className="font-bold mr-1.5">{reply.sender_name}:</span>}
                                {reply.message_text}
                                {reply.attachments && reply.attachments.length > 0 && (
                                  <div className="mt-1 flex gap-1 overflow-x-auto pb-1">
                                    {reply.attachments.map((at, i) => (
                                      <img key={i} src={at.file_url} className="h-10 w-10 object-cover rounded cursor-pointer" onClick={() => setImageModal({url: at.file_url, name: at.file_name})} alt="" />
                                    ))}
                                  </div>
                                )}
                              </div>
                              <span className="text-[8px] text-gray-400 mt-0.5 px-1">
                                {new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Bar - Sticky Bottom */}
        <div className="shrink-0 bg-white border-t border-gray-200 p-3 shadow-[0_-4px_10px_rgba(0,0,0,0.03)]">
          {/* Audience Summary (Collapsible for designers) */}
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              <User size={10} />
              <span>
                To: {composeMeta?.recipientsOverride ? 'Direct' : (isSharedViewer ? getSharedAudienceLabel() : getSingleRecipientLabel(selectedRecipients?.[0]))}
              </span>
            </div>
            {!isSharedViewer && (
              <button 
                onClick={() => setShowNewMessageForm(!showNewMessageForm)}
                className="text-[10px] font-bold text-blue-600 uppercase hover:underline flex items-center gap-1"
              >
                {showNewMessageForm ? 'Hide' : (isDesignerViewer ? 'Chats' : 'Chat With…')}
                {showNewMessageForm ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
              </button>
            )}
          </div>

          {/* Recipient Selection (Toggled) */}
          {!isSharedViewer && showNewMessageForm && (
            <div className="mb-3 p-3 bg-gray-50 rounded-xl border border-gray-200 animate-in fade-in slide-in-from-top-2">
              {composeMeta?.recipientsOverride ? (
                <div className="text-xs text-gray-700">
                  This message is set to <span className="font-semibold">Direct</span> recipients (e.g., schedule request). Send it, or clear your draft to pick a different chat.
                </div>
              ) : (
                <>
                  <div className="mb-2">
                    <input
                      type="text"
                      value={recipientSearchQuery}
                      onChange={(e) => setRecipientSearchQuery(e.target.value)}
                      placeholder="Search trades…"
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-green-500"
                    />
                  </div>

                  {isDesignerViewer && availableRecipients.some(r => (r.email || r.type) === 'all_trades') && (
                    <div className="mb-3">
                      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider px-1 mb-1">Announcements</div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRecipients(['all_trades']);
                          setShowNewMessageForm(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                          selectedRecipients?.[0] === 'all_trades'
                            ? 'border-green-500 bg-green-50 text-green-800'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-green-300'
                        }`}
                      >
                        All Trades (Announcement)
                      </button>
                    </div>
                  )}

                  <div className="mt-3 space-y-2">
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider px-1">Trades</div>
                    <div className="max-h-48 overflow-y-auto recipient-select-scroll space-y-1">
                      {availableRecipients
                        .filter((r) => r.type === 'trade' && r.email)
                        .filter((r) => {
                          const q = recipientSearchQuery.trim().toLowerCase();
                          if (!q) return true;
                          return (r.label || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
                        })
                        .map((recipient) => {
                          const val = recipient.email;
                          const selected = selectedRecipients?.[0] === val;
                          return (
                            <button
                              key={recipient.email}
                              type="button"
                              onClick={() => {
                                setSelectedRecipients([val]);
                                setShowNewMessageForm(false);
                              }}
                              className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                                selected ? 'border-green-500 bg-green-50 text-green-800' : 'border-gray-200 bg-white text-gray-700 hover:border-green-300'
                              }`}
                            >
                              <div className="font-semibold truncate">{recipient.label}</div>
                              <div className="text-[10px] text-gray-500 truncate">{recipient.email}</div>
                            </button>
                          );
                        })}
                      {availableRecipients.filter((r) => r.type === 'trade' && r.email).length === 0 && (
                        <div className="text-xs text-gray-500 px-2 py-2">No trades invited yet.</div>
                      )}
                    </div>
                    {isDesignerViewer && (
                      <div className="text-[11px] text-gray-500 px-1">
                        Tap a trade to open their chat.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Main Input Field */}
          <div className="flex items-end gap-2 bg-gray-50 rounded-2xl p-1 border border-gray-200 focus-within:border-green-400 focus-within:ring-1 focus-within:ring-green-400 transition-all">
            <div className="shrink-0 flex items-center mb-1 ml-1">
              <input
                type="file"
                id="main-chat-img"
                className="hidden"
                multiple
                onChange={handleFileSelect}
                disabled={uploadingAttachments}
              />
              <label htmlFor="main-chat-img" className="p-2.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-full cursor-pointer transition-colors">
                <ImageIcon size={22} />
              </label>
            </div>
            
            <textarea
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                // Auto-expand
                e.target.style.height = 'inherit';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && window.innerWidth > 640) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-2.5 px-1 max-h-[120px] resize-none overflow-y-auto disabled:opacity-60"
              style={{ height: '40px' }}
            />
            
            <button
              onClick={() => handleSendMessage()}
              disabled={
                (!newMessage.trim() && attachments.length === 0) ||
                uploadingAttachments
              }
              className="shrink-0 mb-1 mr-1 p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700 disabled:bg-gray-300 disabled:hover:bg-gray-300 transition-all shadow-md active:scale-95"
            >
              <Send size={20} />
            </button>
          </div>

          {/* Staged Attachments */}
          {attachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 animate-in slide-in-from-bottom-2">
              {attachments.map((at, i) => (
                <div key={i} className="relative w-16 h-16">
                  <img src={at.url} className="w-full h-full object-cover rounded-xl border-2 border-green-100 shadow-sm" alt="" />
                  <button 
                    onClick={() => removeAttachment(i)} 
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal & Image Modal remain the same */}
      {!isSharedViewer && showInviteModal && (
        <InviteCollaboratorModal
          projects={null}
          selectedProjectId={projectId}
          setSelectedProjectId={() => {}}
          defaultProjectId={projectId}
          inviteMode={inviteMode}
          setInviteMode={setInviteMode}
          inviteName={inviteName}
          setInviteName={setInviteName}
          inviteEmail={inviteEmail}
          setInviteEmail={setInviteEmail}
          inviteType={inviteType}
          setInviteType={setInviteType}
          tradeCategory={tradeCategory}
          setTradeCategory={setTradeCategory}
          messageVisibility={messageVisibility}
          setMessageVisibility={setMessageVisibility}
          scheduleAccess={scheduleAccess}
          setScheduleAccess={setScheduleAccess}
          searchQuery={inviteSearchQuery}
          setSearchQuery={setInviteSearchQuery}
          searchLocation={searchLocation}
          setSearchLocation={setSearchLocation}
          searchType={searchType}
          setSearchType={setSearchType}
          searchResults={searchResults}
          searching={searching}
          designerLocation={designerLocation}
          onSearch={handleSearchBusinesses}
          onInviteFromSearch={handleInviteFromSearch}
          onAddBusiness={handleAddBusiness}
          onSubmit={handleInviteCollaborator}
          onClose={() => {
            setShowInviteModal(false);
            setInviteName('');
            setInviteEmail('');
            setInviteType('trade');
            setTradeCategory('contractor');
            setInviteMode('manual');
            setSearchResults([]);
            setInviteSearchQuery('');
            setMessageVisibility('all');
            setScheduleAccess(true);
          }}
        />
      )}

      {imageModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setImageModal(null)}
        >
          <div className="relative max-w-full max-h-full">
            <button
              onClick={() => setImageModal(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl font-bold z-10"
              aria-label="Close"
            >
              ×
            </button>
            <img 
              src={imageModal.url} 
              alt={imageModal.name} 
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {imageModal.name && (
              <p className="text-white text-center mt-3 text-sm font-medium">{imageModal.name}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectMessages;

